
import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { DateService } from "@app/services/date.service";
import { IVeriNavPill } from "@directives/_veri/veriPills/veriPills.component";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell, VeriTableRowStatus } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { DateRange } from "@enums/DateRange.enum";
import { SortType } from "@enums/SortType";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IDateQueryOptions } from "@services/accelerometerRecord.service";
import { DriverService } from "@services/driver.service";
import { DriverAssignmentService } from "@services/driverAssignment.service";
import { GroupService } from "@services/group.service.ajs";
import { TranslationService } from "@services/translation.service";
import { UsersService } from "@services/users.service";
import { VehicleService } from "@services/vehicle.service";
import * as angular from "angular";
import { Subscription } from "rxjs";
import { AlertReviewStatus } from "src/app/main/modules/alerts/_enums/AlertReviewStatus.enums";
import { AlertCategory } from "src/app/main/modules/alerts/_enums/alerts.enums";
import { IAlertRule } from "src/app/main/modules/alerts/_models/alerts.interfaces";
import { IAlertResolutionCode } from "src/app/main/modules/alerts/_models/IAlertResolutionCode";
import { IAlertReviewHistory } from "src/app/main/modules/alerts/_models/IAlertReviewHistory";
import { AlertReviewService } from "src/app/main/modules/alerts/_services/alert-review.service";
import { AlertRecordMaterialisationOptions, AlertsService, IAlertRecordQueryOptions } from "src/app/main/modules/alerts/_services/alerts.service";
import "./alertReviewCompliance.component.scss";


class AlertReviewComplianceComponentController implements angular.IComponentController {
    loading = false;
    tableHeaders: IVeriTableHeader[];
    tableRows: IVeriTableRow[] = [];

    alertReviewHistory: IAlertReviewHistory[];
    alertRules: IAlertRule[];
    depots: IGroupWithIds[];

    defaultDateRange: DateRange = DateRange.Latest7Days;
    dateFilter: IDateFilter;
    groupFilter: IGroupWithIds;

    resolutionCodes: IAlertResolutionCode[];

    navPills: IVeriNavPill<IAlertReviewHistory[]>[];
    tabIndex: number = 0;

    intervalPromise: angular.IPromise<any>;

    subscriptions = new Subscription();

    static $inject = ["$q", "$interval", "$scope", "alertsService", "alertReviewService", "driverService", "driverAssignmentService", "groupService", "translationService", "usersService", "vehicleService", "dateService", "veriTableService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $interval: angular.IIntervalService,
        private readonly $scope: angular.IScope,
        private readonly alertsService: AlertsService,
        private readonly alertReviewService: AlertReviewService,
        private readonly driverService: DriverService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly groupService: GroupService,
        private readonly translationService: TranslationService,
        private readonly usersService: UsersService,
        private readonly vehicleService: VehicleService,
        private readonly dateService: DateService,
        private readonly veriTableService: VeriTableService
    ) {
    }

    $onInit() {

        this.navPills = [
            {
                title: this.translationService.t("Alerts.All"),
                data: [],
                count: 0,
                selected: true
            },
            {
                title: this.translationService.t("Alerts.Open"),
                data: [],
                count: 0
            },
            {
                title: this.translationService.t("Alerts.Closed"),
                data: [],
                count: 0
            }
        ];
        this.dateFilter = this.dateService.dateRangeFromEnum(this.defaultDateRange);
        this.intervalPromise = this.$interval(() => {
            this.loadData(false);
        }, 5 * 60_000)

        this.subscriptions.add(this.alertReviewService.alertReviewHistoryUpdated$.subscribe((history) => {
            this.alertReviewService.updateAlertReviewHistoryItem(this.alertReviewHistory, history);

            this.updateDisplay();
        }));
    }

    $onDestroy(): void {
        this.$interval.cancel(this.intervalPromise);
        this.subscriptions.unsubscribe();
    }

    onDateFilterChanged(filter) {
        this.dateFilter = filter;
        this.loadData(true, true);
    }

    selectedItemChanged(data) {
        this.alertReviewService.openIncidentDetailsDialog(data, 'app.alerts.compliance');
    }

    groupFilterChanged(filter) {

        this.groupFilter = filter.group;

        this.loadData(false);

    }

    onNavPillSelected(selectedNavPill: IVeriNavPill<IAlertReviewHistory[]>) {
        this.tabIndex = this.navPills.indexOf(selectedNavPill);

        this.alertReviewHistory = selectedNavPill.data;
        this.createTableRows();

        // Needed for Hybrid apps
        this.$scope.$apply();
    }

    // ---

    private loadData(showLoadingSpinner = false, invalidateCache = false) {

        if (showLoadingSpinner) {
            this.loading = true;
        }

        this.createTableHeaders();

        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const driversPromise = this.driverService.getDrivers();
        const assignmentsPromise = this.driverAssignmentService.getDriverAssignments(true);
        const groupsPromise = this.groupService.getGroupsWithFullMappings();
        const alertRulesPromise = this.alertsService.getAlertRules();
        const usersPromise = this.usersService.getUsers({ includeMappings: true, includeAdmins: true });
        const resolutionCodesPromise = this.alertsService.getAlertResolutionCodes();

        const dateQueryOptions: IDateQueryOptions = {
            Start: this.dateFilter.start,
            End: this.dateFilter.end,
            invalidateCache: invalidateCache
        }

        const alertReviewHistoryPromise = this.alertReviewService.getAlertReviewHistory(dateQueryOptions);
        const alertReviewEventsPromise = this.alertReviewService.getAlertReviewEvents(dateQueryOptions);

        const alertRecordQueryOptions = dateQueryOptions as IAlertRecordQueryOptions;
        alertRecordQueryOptions.MaterialisationOptions = AlertRecordMaterialisationOptions.WithMetadata;

        const alertRecordsPromise = this.alertsService.getAlertRuleRecordsUsingQueryOptions(alertRecordQueryOptions);

        this.$q.all([vehiclesPromise, groupsPromise, driversPromise, assignmentsPromise, alertReviewHistoryPromise, alertReviewEventsPromise, alertRecordsPromise, alertRulesPromise, usersPromise, resolutionCodesPromise])
            .then(([vehicles, groups, drivers, assignments, alertReviewHistory, alertReviewEvents, alertRecords, alertRules, users, resolutionCodes]) => {
                this.alertReviewHistory = alertReviewHistory;
                this.resolutionCodes = resolutionCodes;
                this.depots = groups.filter(group => group.IsDepot);

                this.driverAssignmentService.mapDriversToVehicles(drivers, assignments, vehicles);

                this.alertRules = alertRules;

                this.alertReviewHistory.forEach(history => {
                    this.alertReviewService.mapVehicleToHistory(history, vehicles);
                    this.alertReviewService.mapEventsToHistory(history, alertReviewEvents);
                    this.alertReviewService.mapAlertRecordToHistory(history, alertRecords);
                    this.alertReviewService.mapUsersToEvents(history, users);

                    if (history.AlertRecord) {
                        this.alertsService.parseAlertRecordMetadata(history.AlertRecord);
                    }

                });

                if (this.groupFilter) {

                    const vehicleIds = this.groupFilter.VehicleIds;
                    this.alertReviewHistory = this.alertReviewHistory.filter(history => {
                        if (vehicleIds.includes(history.VehicleId)) {
                            return history;
                        }
                    })
                }

                this.updateDisplay();

            }).finally(() => {
                this.loading = false;
            });
    }

    private updateDisplay(): void {
        this.setupNavPillData();
        this.alertReviewHistory = this.navPills[this.tabIndex].data;
        this.createTableRows();
    }

    private setupNavPillData() {

        this.navPills[0].data = this.alertReviewHistory;

        this.navPills[1].data = this.alertReviewHistory.filter(alert => {
            return this.alertReviewService.getCurrentStatusOfHistoryItem(alert) === AlertReviewStatus.Open;
        });

        this.navPills[2].data = this.alertReviewHistory.filter(alert => {
            return this.alertReviewService.getCurrentStatusOfHistoryItem(alert) === AlertReviewStatus.Closed;
        });

        this.navPills[0].count = this.navPills[0].data.length;
        this.navPills[1].count = this.navPills[1].data.length;
        this.navPills[2].count = this.navPills[2].data.length;
    }

    private createTableHeaders(): void {
        this.tableHeaders = [
            { title: "ID", sortable: true, sortType: SortType.Number, width: 80 },
            { title: this.translationService.t('Alerts.Status'), sortable: true, graphOptions: { show: true, chartType: 'column', stacked: true, dateColumn: 5, title: this.translationService.t('Alerts.RaisedResolved') }, hideOnMobile: true },
            { title: this.translationService.t('Alerts.Depot'), sortable: true, graphOptions: { show: true, chartType: 'pie' }, hideOnMobile: true },
            { title: this.translationService.t('Alerts.Category'), sortable: true },
            { title: this.translationService.t('Alerts.AlertRaised'), sortable: true, sortType: SortType.Date, hideOnMobile: true },
            { title: this.translationService.t('Alerts.AlertEnd'), sortable: true, sortType: SortType.Date, hideOnMobile: true },
            { title: this.translationService.t('Alerts.OpenDuration'), sortable: true, sortType: SortType.Number },
            { title: this.translationService.t('Alerts.ResCode'), sortable: true, graphOptions: { show: true, chartType: 'pie' }, hideOnMobile: true },
            { title: this.translationService.t('Alerts.ResolvedBy'), sortable: true, hideOnMobile: true }
        ];
    }


    private createTableRows(): void {
        this.tableRows = [];

        this.alertReviewHistory?.forEach(historyItem => {
            const row: IVeriTableRow = {
                data: historyItem,
                cells: this.createTableCells(historyItem)
            };
            row.rowWarningStatus = this.setRowWarningStatus(row, historyItem);
            this.tableRows.push(row);
        });
    }

    private createTableCells(historyItem: IAlertReviewHistory): IVeriTableRowCell[] {
        const status: string = this.translationService.tEnum(AlertReviewStatus, 'AlertReviewStatus', this.alertReviewService.getCurrentStatusOfHistoryItem(historyItem));
        const category: string = this.translationService.tEnum(AlertCategory, 'AlertCategory', this.alertReviewService.getAlertRuleCategory(historyItem, this.alertRules));
        const { subCategoryEnum, subCategoryString } = this.alertsService.getAlertRuleSubcategoryEnum(this.alertReviewService.getAlertRuleCategory(historyItem, this.alertRules));
        const subCategory: string = this.translationService.tEnum(subCategoryEnum, subCategoryString, this.alertReviewService.getAlertRuleSubCategory(historyItem, this.alertRules));

        const depot = this.depots.find(x => x.VehicleIds.includes(historyItem.VehicleId));
        const { startTime, endTime } = this.alertReviewService.getStartAndEndDates(historyItem);
        const openDuration: string = this.dateService.formatDuration(startTime, endTime);

        const user: string = this.alertReviewService.getResolvingUser(historyItem);
        const resCodeId = this.alertReviewService.getResolutionCode(historyItem);
        let resolutionCode: IAlertResolutionCode;

        if (resCodeId) {
            resolutionCode = this.resolutionCodes.find(code => code.Id === resCodeId);
        }

        const cells: IVeriTableRowCell[] = [
            { contents: historyItem.Id.toString(), sortData: historyItem.Id },
            { contents: status },
            depot ? {
                contents: `<depot-name [name]="cell.data.Name"></depot-name>`, exportData: depot?.Name,
                data: depot,
                sortData: depot?.Name,
                graphLabel: depot?.Name
            } : this.veriTableService.createNACell(),
            category ? {
                contents: `<div class="table-category">${category}</div><div class="table-category sub-category">${subCategory ? ' ' + subCategory : ''}</div>`,
                exportData: `${category} ${subCategory ? ' ' + subCategory : ''}`
            } : this.veriTableService.createNACell(),
            this.veriTableService.createDateTimeCell(historyItem.StartTime),
            historyItem.EndTime ? this.veriTableService.createDateTimeCell(historyItem.EndTime) : this.veriTableService.createNACell(),
            openDuration ? {
                contents: openDuration,
                sortData: openDuration
            } : this.veriTableService.createNACell(),
            resolutionCode ? { contents: resolutionCode.Name } : this.veriTableService.createNACell(),
            user ? { contents: user } : this.veriTableService.createNACell()
        ];

        return cells;
    }

    private setRowWarningStatus(row: IVeriTableRow, historyItem: IAlertReviewHistory): VeriTableRowStatus {
        let status: VeriTableRowStatus = VeriTableRowStatus.Error;

        if (this.alertReviewService.getCurrentStatusOfHistoryItem(historyItem) === AlertReviewStatus.Closed) {
            status = VeriTableRowStatus.OkChecked;
        }
        return status;
    }


}

export class AlertReviewComplianceComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: any;

    constructor() {
        this.bindings = {
        };

        this.controller = AlertReviewComplianceComponentController;
        this.template = require("./alertReviewCompliance.component.html");
    }
}

